import * as yup from 'yup'
import { FC, useMemo, useEffect, useCallback } from 'react'
import { AdvisorRequestPanePreset } from 'components/features/coaching/advisorMeeting/AdvisorRequestPanePreset/AdvisorRequestPanePreset'
import { advisorRequestQuestions } from 'components/features/coaching/advisorMeeting/fixtures/requestQuestions'
import { useForm, FormProvider } from 'react-hook-form'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { AdvisorsRequestAction, ResetAdvisorsRequestStateAction } from 'components/features/coaching/advisorStore/actions/advisorMeetingActions'
import { yupResolver } from '@hookform/resolvers/yup'
import { schemaArrayOfStrings, schemaString, schemaNumber } from 'schemas/baseSchema'
import { GetPublicAdvisorTimeslotsAction } from 'components/features/coaching/advisorStore/actions/publicAdvisorActions'
import { zipCodeSchema } from 'helpers/schemaHelpers'
import { TopicStepInputFields, TopicStepOutputFields } from 'components/features/coaching/advisorMeeting/types/floatPane'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { getAdvisorsRequestErrors, timeslotsRequestSelector } from 'components/features/coaching/advisorStore/selectors/landingPage'
import { useServerErrorsApplyToForm } from 'hooks/useServerErrorsApplyToForm'
import { AdvisorRequestTopicForm } from 'components/features/coaching/advisorMeeting/AdvisorRequestTopic/AdvisorRequestTopicForm'
import { AdvisorRequestSpinners } from 'components/features/coaching/advisorMeeting/AdvisorRequestSpinners/AdvisorRequestSpinners'
import { advisorsRequestSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'

const schemaFields = {
  notes: schemaString,
  topics: schemaArrayOfStrings.required(),
}
const regularSchema = yup.object(schemaFields)

const landingSchema = yup.object({
  ...schemaFields,
  income: schemaNumber,
  zip5: zipCodeSchema,
})

type AdvisorRequestTopicProps = {
  fields: TopicStepInputFields
  onClose: () => void
}

export const AdvisorRequestTopic: FC<AdvisorRequestTopicProps> = (props) => {
  const { onClose, fields } = props
  const { flow } = fields
  const formSubmitErrorsOrNull = useStoreSelector(getAdvisorsRequestErrors)

  const timeslotsRequest = useStoreSelector(timeslotsRequestSelector) // For Internal flow
  const advisorsRequest = useStoreSelector(advisorsRequestSelector) // For Public flow

  const isLoading = useMemo(() => {
    if (fields.flow === 'profile') {
      return timeslotsRequest[fields.advisor_uid]?.isLoading
    }

    return advisorsRequest.isLoading
  }, [fields, advisorsRequest, timeslotsRequest])

  const schema = useMemo(() => {
    if (fields.flow === 'landing') {
      return landingSchema
    }

    return regularSchema
  }, [fields])

  const defaultValues = useMemo(() => {
    if (fields.flow === 'landing' && fields.income) {
      return {
        ...(formSubmitErrorsOrNull ? fields : undefined),
        income: fields.income,
      }
    }

    return undefined
  }, [fields, formSubmitErrorsOrNull])

  const formContext = useForm<TopicStepOutputFields>({
    resolver: yupResolver(schema),
    defaultValues,
  })
  const { handleSubmit, watch } = formContext
  const [notes, topics] = watch(['notes', 'topics'])

  const dispatch = useStoreDispatch()
  const handleSuccessSubmit = useMemo(() => handleSubmit((submittedFields) => {
    const nextFields = {
      ...props.fields,
      ...submittedFields,
    }
    if (nextFields.flow === 'profile') {
      dispatch(new GetPublicAdvisorTimeslotsAction(nextFields))
    } else {
      dispatch(new AdvisorsRequestAction(nextFields))
    }
  }), [handleSubmit, dispatch, props.fields])

  const progressPercent = useMemo(() => {
    if (!!isLoading) {
      return 60
    } else if (topics && notes) {
      return 25
    } else if (topics || notes) {
      return 13
    }
    return 5
  }, [topics, notes, isLoading])

  const handleResetRequest = useCallback(() => {
    return dispatch(new ResetAdvisorsRequestStateAction())
  }, [dispatch])

  useServerErrorsApplyToForm(
    formContext,
    formSubmitErrorsOrNull,
  )
  useEffect(() => {
    const resetPreviousRequestState = () => {
      dispatch(new ResetAdvisorsRequestStateAction())
    }

    resetPreviousRequestState()
    return resetPreviousRequestState
  }, [dispatch])

  const contentChild = useMemo(() => {
    return !!isLoading ? (
      <AdvisorRequestSpinners />
    ) : (
      <AdvisorRequestTopicForm fields={ fields } onSubmit={ handleSuccessSubmit } />
    )
  }, [isLoading, fields, handleSuccessSubmit])

  const loadingMessage = 'Finding the perfect coach...'
  const subTitle = flow === 'landing' ? 'Help us find the right coach for you' : `1. ${ advisorRequestQuestions.topic.question }`

  return (
    <AdvisorRequestPanePreset
      title='Schedule a New Coaching Session'
      subTitle={ !!isLoading ? loadingMessage : subTitle }
      actionName={ !!isLoading ? undefined : 'CONTINUE' }
      progressBarPercent={ progressPercent }
      onSuccess={ handleSuccessSubmit }
      onCancel={ !!isLoading ? handleResetRequest : onClose }
    >
      <FormProvider { ...formContext }>
        { contentChild }
      </FormProvider>
    </AdvisorRequestPanePreset>
  )
}
